(() => {
  const openBtnSelector = "[data-inquiry-open]";
  const drawerSelector = "[data-inquiry-drawer]";
  const closeSelector = "[data-inquiry-close]";
  const storageKey = "inquiry:lastProduct";

  function getFocusable(el) {
    return [...el.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((n) => !n.hasAttribute("disabled") && !n.getAttribute("aria-hidden"));
  }

  function safeJsonParse(str) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return null;
    }
  }

  // Saves product info from the inquiry button so it can be restored after Shopify redirects (?contact_posted=true)
  function saveLastProductFromButton(openBtn) {
    if (!openBtn) return;

    const payload = {
      title: openBtn.dataset.productTitle || "",
      url: openBtn.dataset.productUrl || "",
      imageUrl: openBtn.dataset.productImage || "",
      dimensions: openBtn.dataset.productDimensions || "",
      medium: openBtn.dataset.productMedium || ""
    };

    try {
      sessionStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (e) {
      // ignore storage failures
    }
  }

  // Populates drawer UI + hidden inputs
  function populateDrawer(drawer, data) {
    if (!drawer || !data) return;

    const titleEl = drawer.querySelector("[data-inquiry-piece-title]");
    const hiddenProduct = drawer.querySelector("[data-inquiry-hidden-product]");
    const hiddenUrl = drawer.querySelector("[data-inquiry-hidden-url]");
    const imgEl = drawer.querySelector("[data-inquiry-image]");
    const metaEl = drawer.querySelector("[data-inquiry-piece-meta]");

    const title = data.title || "";
    const url = data.url || "";
    const imageUrl = data.imageUrl || "";
    const medium = data.medium || "";
    const dimensions = data.dimensions || "";

    if (titleEl) titleEl.textContent = title;
    if (hiddenProduct) hiddenProduct.value = title;
    if (hiddenUrl) hiddenUrl.value = url;

    // Image
    if (imgEl) {
      if (imageUrl) {
        // Avoid re-setting the same src unnecessarily
        if (imgEl.getAttribute("src") !== imageUrl) imgEl.src = imageUrl;
        imgEl.hidden = false;
      } else {
        imgEl.hidden = true;
        imgEl.removeAttribute("src");
      }
    }

    // Meta line like: "Oil on canvas · 48 x 24 in"
    if (metaEl) {
      const parts = [medium, dimensions].filter(Boolean);
      if (parts.length) {
        metaEl.textContent = parts.join(" · ");
        metaEl.hidden = false;
      } else {
        metaEl.hidden = true;
        metaEl.textContent = "";
      }
    }
  }

  // NEW: Populate from drawer dataset on page load (when product is available in theme.liquid)
  function populateDrawerFromDataset(drawer) {
    if (!drawer) return;

    populateDrawer(drawer, {
      title: drawer.dataset.productTitle || "",
      url: drawer.dataset.productUrl || "",
      imageUrl: drawer.dataset.productImage || "",
      medium: drawer.dataset.productMedium || "",
      dimensions: drawer.dataset.productDimensions || ""
    });
  }

  function openDrawer(drawer, opener) {
    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("inquiry-drawer-lock");

    drawer._opener = opener;

    const panel = drawer.querySelector(".inquiry-drawer__panel");
    if (panel) panel.scrollTop = 0; // Always start at top when opening (prevents mobile weirdness)

    const focusable = panel ? getFocusable(panel) : [];
    (focusable[0] || panel || drawer).focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDrawer(drawer);

      if (e.key === "Tab" && panel) {
        const items = getFocusable(panel);
        if (!items.length) return;

        const first = items[0];
        const last = items[items.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    drawer._onKeyDown = onKeyDown;
    document.addEventListener("keydown", onKeyDown);

    // Preserve existing behavior: update visible title from button dataset (harmless, but keeps parity)
    const pieceTitle = drawer.querySelector("[data-inquiry-piece-title]");
    if (pieceTitle && opener?.dataset?.productTitle) {
      pieceTitle.textContent = opener.dataset.productTitle;
    }
  }

  function closeDrawer(drawer) {
    drawer.classList.remove("is-open");
    drawer.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("inquiry-drawer-lock");

    if (drawer._onKeyDown) document.removeEventListener("keydown", drawer._onKeyDown);

    const opener = drawer._opener;
    drawer._opener = null;

    const panel = drawer.querySelector(".inquiry-drawer__panel");

    // If panel is missing for any reason, just focus immediately
    if (!panel) {
      if (opener) opener.focus();
      return;
    }

    const onDone = () => {
      panel.removeEventListener("transitionend", onDone);
      if (opener) opener.focus();
    };

    panel.addEventListener("transitionend", onDone);

    // Fallback: if transitionend doesn't fire, restore focus anyway
    window.setTimeout(() => {
      panel.removeEventListener("transitionend", onDone);
      if (opener) opener.focus();
    }, 350);
  }

  function init() {
    const drawer = document.querySelector(drawerSelector);
    if (!drawer) return;

    // Populate once on page load from the drawer's dataset (your new HTML supports this)
    populateDrawerFromDataset(drawer);

    const collectorSelect = document.getElementById("InquiryCollectorType");

    if (collectorSelect) {
      collectorSelect.addEventListener("change", function () {
        if (this.value === "") {
          this.classList.add("select-placeholder");
        } else {
          this.classList.remove("select-placeholder");
        }
      });
    }

    document.addEventListener("click", (e) => {
      const openBtn = e.target.closest(openBtnSelector);
      if (openBtn) {
        e.preventDefault();
        const drawer = document.querySelector(drawerSelector);
        if (!drawer) return;

        // Keep: Save last product from button so post-redirect can restore (mobile reliable)
        saveLastProductFromButton(openBtn);

        // We do NOT need to populate here anymore (it’s done on load),
        // but it’s safe to refresh the drawer contents from the button anyway
        // in case the page has multiple inquiry buttons or dynamic changes.
        populateDrawer(drawer, {
          title: openBtn.dataset.productTitle || "",
          url: openBtn.dataset.productUrl || "",
          imageUrl: openBtn.dataset.productImage || "",
          medium: openBtn.dataset.productMedium || "",
          dimensions: openBtn.dataset.productDimensions || ""
        });

        openDrawer(drawer, openBtn);
        return;
      }

      const closeBtn = e.target.closest(closeSelector);
      if (closeBtn && drawer.classList.contains("is-open")) {
        e.preventDefault();
        closeDrawer(drawer);
      }
    });
  }

  // Handle post-submit state: open drawer, show success, restore product info, scroll to top
  document.addEventListener("DOMContentLoaded", () => {
    const params = new URLSearchParams(window.location.search);
    const wasPosted = params.get("contact_posted") === "true";
    if (!wasPosted) return;

    const drawer = document.querySelector(drawerSelector);
    if (!drawer) return;

    // First: try dataset (works when product is available at theme.liquid scope)
    populateDrawerFromDataset(drawer);

    // Then: prefer sessionStorage restore (most reliable across redirects)
    const stored = safeJsonParse(sessionStorage.getItem(storageKey) || "null");
    if (stored) populateDrawer(drawer, stored);

    const successMessage = drawer.querySelector("[data-inquiry-success]");
    if (successMessage) successMessage.hidden = false;

    // Hide the form after a successful submit (as you had)
    const form = drawer.querySelector("#InquiryForm");
    if (form) form.hidden = true;

    drawer.classList.add("is-open");
    drawer.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("inquiry-drawer-lock");

    const panel = drawer.querySelector(".inquiry-drawer__panel");
    if (panel) panel.scrollTop = 0;

    // Remove anchor jump so browser doesn't scroll the panel/page down to #InquiryForm
    if (window.location.hash === "#InquiryForm") {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }

    // Remove query string so refresh doesn't reopen it again
    const url = new URL(window.location);
    url.searchParams.delete("contact_posted");
    window.history.replaceState({}, "", url);
  });

  document.addEventListener("DOMContentLoaded", init);
})();